import { faDownload, faEye, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import FileSaver from "file-saver";
import React, { useEffect, useState } from "react";
import { Button, Spinner, Stack } from "react-bootstrap";
import { useParams } from "react-router-dom";
import {
  deleteDocument,
  deleteNoteDocument,
  fetchDocumentDownload,
  fetchDocumentView,
  fetchNoteDocumentDownload,
  fetchNoteDocumentView,
} from "../../../../services/httpClient";
import IconEmbeddedButton from "../../../ui/IconEmbeddedButton";
import { Tooltip } from "react-tooltip";

interface ViewFilesRowState {
  item: any;
  fetchDocuments: any;
  myCase: boolean;
  setIsLoading: any;
  type: string;
  setShowToast?: any;
  setToastText?: any;
  setToastBody?: any;
}

export const ViewFilesRow = ({ item, fetchDocuments, myCase, setIsLoading, type, setShowToast, setToastText, setToastBody }: ViewFilesRowState) => {
  const { id } = useParams();
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [docToDelete, setDocToDelete] = useState<string>();
  const [canView, setCanView] = useState(true);
  const isSafariOniPad = () => {
    const ua = navigator.userAgent.toLowerCase();

    const isTouchDevice = "ontouchstart" in window || navigator.maxTouchPoints > 0;

    const isSafari = /^((?!chrome|android).)*safari/.test(ua);
    const isiPad = /ipad/.test(ua) || (navigator.userAgent.includes("Macintosh") && isTouchDevice);

    return isiPad && isSafari;
  };

  useEffect(() => {
    const extensions = ["jpg", "JPG", "jpeg", "JPEG", "png", "PNG", "pdf", "PDF"];

    if (extensions.some((extension) => item.name.split(".").pop() === extension)) {
      setCanView(true);
    } else {
      setCanView(false);
    }
  }, [item.name]);

  const onClickDownload = (docId: string, docName: string) => {
    if (!isSafariOniPad()) {
      setIsDownloading(true);
    }

    if (type === "Case") {
      fetchDocumentDownload(id, docId)
        .then((res: any) => {
          FileSaver.saveAs(res[0].data, docName);
        })
        .finally(() => {
          setIsDownloading(false);
        });
    } else if (type === "Note") {
      fetchNoteDocumentDownload(id, item?.note_id, docId)
        .then((res: any) => {
          FileSaver.saveAs(res[0].data, docName);
        })
        .finally(() => {
          setIsDownloading(false);
        });
    }
  };

  const onClickView = (docId: string, docName: string) => {
    if (type === "Case") {
      fetchDocumentView(id, docId).then((res: any) => {
        const blobUrl = URL.createObjectURL(res[0].data);
        window.open(blobUrl, "_blank");
      });
    } else if (type === "Note") {
      fetchNoteDocumentView(id, item?.note_id, docId).then((res: any) => {
        const blobUrl = URL.createObjectURL(res[0].data);
        window.open(blobUrl, "_blank");
      });
    }
  };

  const onClickDelete = (docId: string) => {
    setIsDeleting(true);
    setDocToDelete(docId);
  };

  const onClickDeleteFinal = async () => {
    setIsLoading(true);

    if (type === "Case") {
      deleteDocument(id, docToDelete).then((res: any) => {
        fetchDocuments();
        setIsDeleting(false);
        setShowToast(true);
        setToastText("Document Deleted");
        setToastBody(item?.name);
      });
    } else {
      deleteNoteDocument(id, item?.note_id, docToDelete).then((res: any) => {
        fetchDocuments();
        setIsDeleting(false);
        setShowToast(true);
        setToastText("Note Document Deleted");
        setToastBody(item?.name);
      });
    }
  };

  const onClickCancelDelete = () => {
    setIsDeleting(false);
    setDocToDelete(undefined);
  };

  const renderDelete = () => {
    return (
      <tr>
        <td colSpan={1}>Are you sure you want to delete?</td>
        <td colSpan={2} className="text-end">
          <Button className="me-3 app-danger-color button" onClick={() => onClickDeleteFinal()}>
            Confirm Delete
          </Button>
          <Button className="app-primary-color button" onClick={() => onClickCancelDelete()}>
            Cancel
          </Button>
        </td>
      </tr>
    );
  };

  return (
    <>
      {isDeleting ? (
        <>{renderDelete()}</>
      ) : (
        <>
          <tr key={item.id}>
            <td className="font-size-dynamic-larger">{item.name}</td>
            <td className="font-size-dynamic-larger">{item.created_at}</td>
            <td className="text-end">
              <Stack direction="horizontal" gap={2} className="justify-content-end">
                {canView && (
                  <>
                    <Button
                      data-tooltip-id="tooltip-doc-view"
                      data-tooltip-content="View"
                      className="app-primary-color button button-smaller d-flex justify-content-center align-items-center p-2"
                      onClick={() => (isSafariOniPad() ? onClickDownload(item.id, item.name) : onClickView(item.id, item.name))}
                    >
                      <FontAwesomeIcon icon={faEye} />
                      <Tooltip id="tooltip-doc-view" place="top" style={{ padding: "5px 10px", zIndex: "1100" }} />
                    </Button>
                  </>
                )}
                <Button
                  data-tooltip-id="tooltip-doc-download"
                  data-tooltip-content="Download"
                  className="app-tertiary-color button button-smaller d-flex justify-content-center align-items-center"
                  onClick={() => onClickDownload(item.id, item.name)}
                >
                  {isDownloading ? (
                    <Spinner className="spinner-small" animation="border" style={{ color: "#FFFFFF" }} />
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faDownload} />
                      <Tooltip id="tooltip-doc-download" place="top" style={{ padding: "5px 10px", zIndex: "1100" }} />
                    </>
                  )}
                </Button>

                {myCase && (
                  <IconEmbeddedButton onClickButton={() => onClickDelete(item.id)} tooltipText="Delete" icon={faTrash} color="danger" />
                )}
              </Stack>
            </td>
          </tr>
        </>
      )}
    </>
  );
};
